import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './PlannerAdmin.css';

interface Customer {
  id: number;
  name: string;
}

interface Plan {
  id: number;
  name: string;
  customer: number;
  customer_name: string;
  location_name: string;
  year: number;
  status: string;
  status_display: string;
  preferred_cadence: string;
  cadence_display: string;
  batch_count: number;
  active_batch_count: number;
  created_at: string;
}

const SeasonalPlansAdmin: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());

  useEffect(() => {
    fetchPlans();
    fetchCustomers();
  }, [statusFilter, customerFilter, yearFilter]);

  const fetchPlans = async () => {
    try {
      const params: any = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (customerFilter !== 'all') params.customer = customerFilter;
      if (yearFilter !== 'all') params.year = yearFilter;

      const response = await api.get('/planner/admin/plans/', { params });
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers/');
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const handleActivate = async (planId: number) => {
    try {
      await api.post(`/planner/admin/plans/${planId}/activate/`);
      fetchPlans();
    } catch (error) {
      console.error('Failed to activate plan:', error);
    }
  };

  const handleGenerateOrders = async (planId: number) => {
    try {
      const response = await api.post(`/planner/admin/plans/${planId}/generate_orders/`);
      alert(`Generated ${response.data.orders_created} orders`);
      fetchPlans();
    } catch (error) {
      console.error('Failed to generate orders:', error);
      alert('Failed to generate orders');
    }
  };

  if (loading) {
    return <div className="planner-admin-loading">Loading plans...</div>;
  }

  return (
    <div className="plans-admin-page">
      <div className="page-header">
        <h1>Seasonal Plans</h1>
        <div>
          <Link to="/planner" className="btn-secondary" style={{ marginRight: '12px' }}>
            ← Back
          </Link>
          <Link to="/planner/plans/new" className="btn-primary">
            + New Plan
          </Link>
        </div>
      </div>

      <div className="filters-bar">
        <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
          <option value="all">All Years</option>
          <option value="2025">2025</option>
          <option value="2024">2024</option>
          <option value="2023">2023</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
        </select>
        <select value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}>
          <option value="all">All Customers</option>
          {customers.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Plan Name</th>
              <th>Customer</th>
              <th>Location</th>
              <th>Year</th>
              <th>Status</th>
              <th>Cadence</th>
              <th>Batches</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map(plan => (
              <tr key={plan.id}>
                <td>
                  <Link to={`/planner/plans/${plan.id}`} style={{ color: '#8B0000', fontWeight: 500 }}>
                    {plan.name}
                  </Link>
                </td>
                <td>{plan.customer_name}</td>
                <td>{plan.location_name || 'Not set'}</td>
                <td>{plan.year}</td>
                <td>
                  <span className={`status-badge ${plan.status}`}>
                    {plan.status_display}
                  </span>
                </td>
                <td>{plan.cadence_display}</td>
                <td>
                  {plan.active_batch_count} active / {plan.batch_count} total
                </td>
                <td className="actions-cell">
                  <Link to={`/planner/plans/${plan.id}`} className="btn-small view">
                    View
                  </Link>
                  {plan.status === 'draft' && (
                    <button
                      className="btn-small edit"
                      onClick={() => handleActivate(plan.id)}
                    >
                      Activate
                    </button>
                  )}
                  {plan.status === 'active' && (
                    <button
                      className="btn-small edit"
                      onClick={() => handleGenerateOrders(plan.id)}
                    >
                      Gen Orders
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {plans.length === 0 && (
          <p style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            No plans found matching your filters
          </p>
        )}
      </div>
    </div>
  );
};

export default SeasonalPlansAdmin;
